import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import type { ReportCompleteness } from "@normax-performance/shared-types";

export interface ReportCompletenessFooterProps {
  completeness: ReportCompleteness;
}

/** Mandatory on every report (task spec §21) — never implies attendance,
 * strictly "active flag vs. recorded entry presence," same wording contract
 * as `CompletenessSignal.tsx` (Dashboard).
 *
 * premium-presentation-polish: styled as a document footer/quality block —
 * a quiet checkmark when complete, a contained (not full-red-alarm) warning
 * callout when not, plus a compact 3-stat row (recorded/without-entry/draft)
 * alongside the original full sentence, which stays exactly as before for
 * screen readers and existing test coverage. */
export function ReportCompletenessFooter({ completeness }: ReportCompletenessFooterProps) {
  const { t } = useTranslation("reports");
  return (
    <section className={`nx-report-completeness${completeness.isIncomplete ? " nx-report-completeness--incomplete" : ""}`} aria-label={t("completeness.title")}>
      <p className="nx-report-panel__title">{t("completeness.title")}</p>
      <p className="nx-report-completeness__status">
        {completeness.isIncomplete ? (
          <AlertTriangle size={16} aria-hidden="true" className="nx-report-completeness__status-icon" />
        ) : (
          <CheckCircle2 size={16} aria-hidden="true" className="nx-report-completeness__status-icon" />
        )}
        <span className={completeness.isIncomplete ? "nx-report-completeness__warning" : undefined}>
          {t(completeness.isIncomplete ? "completeness.incomplete" : "completeness.complete")}
        </span>
      </p>
      <div className="nx-report-completeness__stats">
        <span className="nx-report-completeness__stat">
          <span className="nx-report-completeness__stat-value">{completeness.recordedWorkerCount}</span>
          <span className="nx-report-completeness__stat-label">{t("completeness.labels.recorded")}</span>
        </span>
        <span className="nx-report-completeness__stat">
          <span className="nx-report-completeness__stat-value">{completeness.activeWorkersWithoutEntryCount}</span>
          <span className="nx-report-completeness__stat-label">{t("status.noEntry")}</span>
        </span>
        <span className="nx-report-completeness__stat">
          <span className="nx-report-completeness__stat-value">{completeness.draftEntryCount}</span>
          <span className="nx-report-completeness__stat-label">{t("status.draft")}</span>
        </span>
      </div>
      <p className="nx-report-completeness__detail">
        {t("completeness.summary", {
          recordedCount: completeness.recordedWorkerCount,
          activeCount: completeness.activeWorkerCount,
          withoutEntryCount: completeness.activeWorkersWithoutEntryCount,
          draftCount: completeness.draftEntryCount,
        })}
      </p>
    </section>
  );
}
